import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import { pocetnaZaUlogu } from '../app/rute'
import { LanguageSwitcher } from '../components/Jezik'
import { prijaviOsoblje } from '../auth/session'
import { useSession } from '../auth/useSession'
import { Button } from '../components/Button'
import { TextField } from '../components/Field'
import { DNO_EKRANA, VRH_EKRANA } from '../components/ljuska'
import { Znak } from '../components/Znak'
import { porukaGreske, porukaPolja } from '../i18n/greske'
import { Obrazac } from '../components/Obrazac'

/**
 * `POST /auth/staff/login` (API §1). Osoblje ne skenira QR, pa mu je jedini
 * izvor tenanta subdomena resorta — i to je jedino mjesto u cijeloj aplikaciji
 * na kojem tenant dolazi izvan tokena (CLAUDE.md §4).
 */
export function LoginPage() {
  const { t } = useTranslation()
  const sesija = useSession()

  const [subdomena, postaviSubdomenu] = useState('')
  const [email, postaviEmail] = useState('')
  const [lozinka, postaviLozinku] = useState('')
  const [greska, postaviGresku] = useState<unknown>(null)
  const [salje, postaviSlanje] = useState(false)

  if (sesija.accessToken) {
    return <Navigate to={pocetnaZaUlogu(sesija.uloga)} replace />
  }

  async function posalji(dogadaj: FormEvent) {
    dogadaj.preventDefault()
    postaviGresku(null)
    postaviSlanje(true)

    try {
      await prijaviOsoblje(subdomena.trim().toLowerCase(), email.trim(), lozinka)
    } catch (kvar) {
      postaviGresku(kvar)
    } finally {
      postaviSlanje(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-noir">
      <div className="flex justify-end px-4 pb-4" style={{ paddingTop: VRH_EKRANA }}>
        <LanguageSwitcher />
      </div>

      <main className="flex grow items-start justify-center px-4" style={{ paddingBottom: DNO_EKRANA }}>
        <Obrazac
          naSlanje={(dogadaj) => void posalji(dogadaj)}
          className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6"
        >
          {/* Znak marke stoji ondje gdje se aplikacija predstavlja (Z15). */}
          <div className="mb-2 flex justify-center">
            <Znak velicina={40} />
          </div>
          <p className="text-center text-[10px] tracking-[0.3em] text-brass uppercase">
            {t('app.name')}
          </p>
          <h1 className="mt-1 text-center text-2xl text-ivory">{t('auth.login.title')}</h1>
          <p className="mt-1 mb-6 text-center text-sm text-stone">{t('auth.login.lead')}</p>

          <div className="flex flex-col gap-4 [&_label]:text-brass [&_input]:border-line [&_input]:bg-noir [&_input]:text-ivory [&_p]:text-stone">
            <TextField
              label={t('auth.login.subdomena')}
              hint={t('auth.login.subdomenaHint')}
              greska={porukaPolja(t, greska, 'subdomena')}
              value={subdomena}
              onChange={(dogadaj) => postaviSubdomenu(dogadaj.target.value)}
              autoComplete="organization"
              autoCapitalize="none"
              spellCheck={false}
              required
            />

            <TextField
              label={t('auth.login.email')}
              greska={porukaPolja(t, greska, 'email')}
              type="email"
              value={email}
              onChange={(dogadaj) => postaviEmail(dogadaj.target.value)}
              autoComplete="username"
              required
            />

            <TextField
              label={t('auth.login.lozinka')}
              greska={porukaPolja(t, greska, 'lozinka')}
              type="password"
              value={lozinka}
              onChange={(dogadaj) => postaviLozinku(dogadaj.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          {greska ? (
            <p role="alert" className="mt-4 text-sm text-champagne">
              {porukaGreske(t, greska)}
            </p>
          ) : null}

          <Button izgled="gold" type="submit" className="mt-6 w-full" disabled={salje}>
            {salje ? t('auth.login.working') : t('auth.login.submit')}
          </Button>

          <p className="mt-4 text-center text-xs text-stone">{t('auth.login.guestHint')}</p>
        </Obrazac>
      </main>
    </div>
  )
}
